import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  MenuItem,
  Step,
  Stepper,
  StepLabel,
  CircularProgress,
} from '@mui/material';

const PERMIT_TYPES = [
  'Building Plan',
  'Electrical Plan',
  'Plumbing Plan',
  'Mechanical Plan',
  'Demolition Plan',
  'Grading Plan',
  'Site Development Plan',
  'Other'
];

interface CreatePermitDialogProps {
  open: boolean;
  onClose: () => void;
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  handleSubmit: () => void;
  permitData: {
    permitType: string;
    estimatedTimeline: string;
  };
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedFiles: File[];
  uploadError: string | null;
  getRootProps: any;
  getInputProps: any;
  isSubmitting: boolean;
  onSubmit: (data: any) => void;
  PaperProps?: {
    sx?: {
      [key: string]: any;
    };
  };
  sx?: {
    [key: string]: any;
  };
}

const CreatePermitDialog: React.FC<CreatePermitDialogProps> = ({
  open,
  onClose,
  activeStep,
  handleNext,
  handleBack,
  handleSubmit,
  permitData,
  handleInputChange,
  selectedFiles,
  uploadError,
  getRootProps,
  getInputProps,
  isSubmitting,
  onSubmit,
  PaperProps,
  sx,
}) => {
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              select
              label="Plan Type"
              name="permitType"
              value={permitData.permitType}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            >
              {PERMIT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            
          </Box>
        );
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                mb: 2,
              }}
            >
              <input {...getInputProps()} />
              <p>Drag and drop files here, or click to select files</p>
              <em>(Only PDF, DOC, DOCX, JPG, JPEG, and PNG files are accepted)</em>
            </Box>
            {selectedFiles.length > 0 && (
              <Box>
                <p>Selected files:</p>
                <ul>
                  {selectedFiles.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              </Box>
            )}
            {uploadError && (
              <Box sx={{ color: 'error.main', mt: 2 }}>
                {uploadError}
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        }
      }}
    >
      <DialogTitle>Upload New Plan</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ py: 3 }}>
          <Step>
            <StepLabel>Plan Details</StepLabel>
          </Step>
          <Step>
            <StepLabel>Upload Documents</StepLabel>
          </Step>
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button 
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={activeStep === 1 ? handleSubmit : handleNext}
          disabled={
            isSubmitting ||
            (activeStep === 0 && (!permitData.permitType )) ||
            (activeStep === 1 && selectedFiles.length === 0)
          }
          startIcon={isSubmitting && activeStep === 1 ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting && activeStep === 1 ? 'Uploading...' : activeStep === 1 ? 'Submit' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePermitDialog; 